import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { Snippet } from '@/components/common/Snippet';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { Figure, LoadMore, NameLink, caseHref, directorHref } from '@/pages/cvm/shared';
import { fetchCvmDecisions } from '@/services/cvm';
import type { CvmSearchHit } from '@/services/types';

const PAGE_SIZE = 25;
const SEGMENTS = ['extrato', 'relatorio', 'voto'] as const;

/**
 * Portuguese full text over the judgment documents.
 *
 * The extrato of every judgment with a text layer, plus the Relatório and the
 * Voto where the PDF binds them. The reach panel says how many documents carry
 * each segment, because a search restricted to the Voto covers a small subset
 * and an empty result there is a claim about that subset only. Highlights are
 * sentinel markers the client escapes; a query without a searchable term is
 * reported as such rather than as an empty corpus.
 */
export function CvmDecisionsPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('q') ?? '');
  const [q, setQ] = useState<string | null>(seed.get('q') || null);
  const [segment, setSegment] = useState(seed.get('segment') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [relator, setRelator] = useState(seed.get('relator') ?? '');
  const filters = {
    q,
    segment: segment || null,
    year: year ? Number(year) : null,
    relator: relator || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'decisions', filters],
    queryFn: ({ pageParam }) =>
      fetchCvmDecisions({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.hits.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const hits = paged?.pages.flatMap((page) => page.hits) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:decisions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:decisions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:decisions.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="cvm-decision-query" className="text-xs font-medium text-foreground">
            {t('cvm:decisions.search.label')}
          </label>
          <input
            id="cvm-decision-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('cvm:decisions.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="cvm-segment" className="block text-xs font-medium text-foreground">
            {t('cvm:decisions.filters.segment')}
          </label>
          <select
            id="cvm-segment"
            value={segment}
            onChange={(event) => setSegment(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('cvm:decisions.filters.anySegment')}</option>
            {SEGMENTS.map((s) => (
              <option key={s} value={s}>
                {t(`cvm:segments.${s}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="cvm-decision-year" className="block text-xs font-medium text-foreground">
            {t('cvm:decisions.filters.year')}
          </label>
          <input
            id="cvm-decision-year"
            inputMode="numeric"
            value={year}
            onChange={(event) => setYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
            className="w-24 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label
            htmlFor="cvm-decision-relator"
            className="block text-xs font-medium text-foreground"
          >
            {t('cvm:decisions.filters.relator')}
          </label>
          <input
            id="cvm-decision-relator"
            value={relator}
            onChange={(event) => setRelator(event.target.value)}
            placeholder={t('cvm:decisions.filters.relatorPlaceholder')}
            className="w-48 rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('cvm:decisions.search.submit')}
        </button>
      </form>

      {data ? (
        <section className="rounded-lg border bg-card p-4" data-reach>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:decisions.reach.title')}
          </h2>
          <Answer answer={data.reach} help={t('cvm:decisions.reach.help')}>
            {(reach) => (
              <div className="space-y-2">
                {/* Both warnings sit ABOVE the count: below it, "0 matched" has
                    already been read as an answer about the corpus. */}
                {reach.no_searchable_terms ? (
                  <p
                    role="alert"
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-foreground"
                  >
                    {t('cvm:decisions.reach.noTerms')}
                  </p>
                ) : null}
                {reach.query_unaccented ? (
                  <p
                    role="alert"
                    className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-foreground"
                  >
                    {t('cvm:decisions.reach.unaccented')}
                  </p>
                ) : null}
                <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <Figure
                    label={t('cvm:decisions.reach.matched')}
                    value={number.format(reach.matched)}
                  />
                  <Figure
                    label={t('cvm:decisions.reach.searchable')}
                    value={number.format(reach.searchable)}
                  />
                  <Figure
                    label={t('cvm:decisions.reach.decisions')}
                    value={number.format(reach.decisions)}
                  />
                  <Figure
                    label={t('cvm:decisions.reach.withRelatorio')}
                    value={number.format(reach.with_relatorio)}
                  />
                  <Figure
                    label={t('cvm:decisions.reach.withVoto')}
                    value={number.format(reach.with_voto)}
                  />
                </dl>
              </div>
            )}
          </Answer>
        </section>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={6} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={6} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(hits.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className="max-h-[65vh] space-y-2 overflow-auto rounded-lg border p-2"
          >
            <ol className="space-y-2">
              {hits.map((hit) => (
                <HitRow key={hit.case_key} hit={hit} />
              ))}
            </ol>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      <FieldGuide i18nKey="cvm:decisions.guide" />
    </div>
  );
}

function HitRow({ hit }: { hit: CvmSearchHit }) {
  const { t } = useTranslation(['cvm']);
  return (
    <li className="rounded-md border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link to={caseHref(hit.case_key)} className="font-medium text-primary hover:underline">
          {hit.process_number}
        </Link>
        <span className="text-xs text-muted-foreground">
          {hit.judgment_date ?? '—'}
          {' · '}
          <NameLink name={hit.relator} href={directorHref(hit.relator_slug)} />
          {hit.has_relatorio ? ` · ${t('cvm:segments.relatorio')}` : ''}
          {hit.has_voto ? ` · ${t('cvm:segments.voto')}` : ''}
        </span>
      </div>
      {hit.ementa ? <p className="mt-1 text-xs text-muted-foreground">{hit.ementa}</p> : null}
      {hit.snippet ? (
        <p className="mt-1 text-xs text-foreground">
          <Snippet text={hit.snippet} />
        </p>
      ) : null}
    </li>
  );
}
